// Conditions with durations. The initiative clock (Next turn, a jump, or the
// Roll20 follow) calls tickConditions on every forward turn change: the
// character whose turn ended loses one turn from its "end" conditions, the
// one whose turn begins loses one from its "start" conditions; at 0 the
// condition is removed and the removal flows to the sheet and the token.
import type { Character, Condition } from "./types";
import { tickAuras, untickAuras } from "./auras";
import { tickLight, untickLight } from "./lighting";
import { normLoose } from "./text";

/** The per-row store key for a tracker entry that is NOT a party member (its
 *  conditions, lights, ally flag, defences). A real Roll20 token keys by id;
 *  a Compass-only or hand-typed row keys by its forgiving-normalised name.
 *  Initiative WRITES these keys, so anything that reads them back — the token
 *  tooltip push on a concentration drop among them — must key identically. */
export const allyKey = (e: { tokenId?: string; name: string }) =>
  e.tokenId && e.tokenId !== "-1" ? `t:${e.tokenId}` : `n:${normLoose(e.name)}`;

export const conditionName = (c: string | Condition) => typeof c === "string" ? c : String(c.name || c.condition || "");
export const conditionLabel = (c: string | Condition) => {
  const name = conditionName(c);
  if (typeof c === "string" || c.remaining === undefined || c.remaining === null) return name;
  return `${name} (${c.remaining})`;
};
/** Hover text: how long is left and whose turn it counts on. */
export const conditionTitle = (c: string | Condition) => typeof c === "string" || c.remaining === undefined || c.remaining === null ? "Until removed" : `${c.remaining} more round${c.remaining === 1 ? "" : "s"}, ending at the ${c.phase || "end"} of ${c.anchorName ? c.anchorName + "'s" : "their"} turn`;
export function withDuration(name: string, remaining: number | null, phase: "start" | "end", anchor?: { id: string; name: string } | null): string | Condition {
  if (!remaining || remaining <= 0) return name;
  return { name, remaining: Math.round(remaining), total: Math.round(remaining), phase, ...(anchor ? { anchorId: anchor.id, anchorName: anchor.name } : {}) };
}
/** Rounds · minutes (10 rounds) · hours (600). A round = one turn of the condition's anchor. */
export const TURNS_PER: Record<"rounds" | "minutes" | "hours", number> = { rounds: 1, minutes: 10, hours: 600 };
/** The turn a condition counts on: its anchor, else the creature carrying it. */
export const anchorOf = (cond: Condition, ownerId: string) => String(cond.anchorId || ownerId);
/** Drop the conditions a given caster's CONCENTRATION left on a creature (a plain-string
 *  condition, or one with no concentration source, is always kept). Lifted out of the
 *  concentration-loss handler so the PC list, the NPC list and the token tooltip all apply
 *  the exact same rule. */
export function dropConcentration(list: (string | Condition)[] | undefined, casterId: string): (string | Condition)[] {
  return (list || []).filter((y) => {
    if (typeof y === "string") return true;
    const src = y.source;
    return !src || src.casterId !== casterId || !src.concentration;
  });
}
/** Apply dropConcentration to every NPC/creature row and report which rows changed, so the
 *  caller can refresh just those tokens' Roll20 tooltips. The party bar-sync covers PC tokens;
 *  NPC tooltips are pushed per-change, so a concentration drop that cleared an NPC's condition
 *  must push too or the token's hover keeps a condition that has ended. */
export function concentrationDropNpc(
  npcConditions: Record<string, (string | Condition)[]> | undefined,
  casterId: string,
): { next: Record<string, (string | Condition)[]>; changed: { key: string; conds: (string | Condition)[] }[] } {
  const next: Record<string, (string | Condition)[]> = {};
  const changed: { key: string; conds: (string | Condition)[] }[] = [];
  for (const [k, list] of Object.entries(npcConditions || {})) {
    const conds = dropConcentration(list, casterId);
    next[k] = conds;
    if (conds.length !== (list || []).length) changed.push({ key: k, conds });
  }
  return { next, changed };
}
/** Count down one condition list for the turn that moved (`endedId` → `startedId`). */
export function tickList(list: (string | Condition)[], ownerId: string, endedId: string, startedId: string): { conditions: (string | Condition)[]; expired: string[] } {
  const expired: string[] = [];
  const conditions = list.flatMap((cond) => {
    if (typeof cond === "string" || cond.remaining === undefined || cond.remaining === null) return [cond];
    const on = anchorOf(cond, ownerId);
    const hit = ((cond.phase || "end") === "end" && on === endedId) || ((cond.phase || "end") === "start" && on === startedId);
    if (!hit) return [cond];
    const left = Number(cond.remaining) - 1;
    if (left <= 0) { expired.push(conditionName(cond)); return []; }
    return [{ ...cond, remaining: left }];
  });
  return { conditions, expired };
}
export function untickList(list: (string | Condition)[], ownerId: string, endedId: string, startedId: string): (string | Condition)[] {
  return list.map((cond) => {
    if (typeof cond === "string" || cond.remaining === undefined || cond.remaining === null) return cond;
    const on = anchorOf(cond, ownerId);
    const hit = ((cond.phase || "end") === "end" && on === endedId) || ((cond.phase || "end") === "start" && on === startedId);
    if (!hit) return cond;
    const cap = Number(cond.total || 0) || Infinity;
    return { ...cond, remaining: Math.min(cap, Number(cond.remaining) + 1) };
  });
}

export interface TickResult { characters: Character[]; expired: { characterName: string; condition: string }[]; aurasEnded: { characterName: string; aura: string }[]; lightsOut: { characterName: string; light: string }[] }

/** The turn moved back from `startedId` to `endedId`: undo one forward tick. */
export function untickConditions(characters: Character[], endedId: string, startedId: string): Character[] {
  return characters.map((c) => {
    let out = c;
    if (c.id === startedId && (c.auras || []).length) out = { ...out, auras: untickAuras(out) };
    if (c.id === startedId && c.light) out = { ...out, light: untickLight(out) };
    if ((c.conditions || []).length) { const next = untickList(out.conditions || [], c.id, endedId, startedId); if (next.some((x, i) => x !== (out.conditions || [])[i])) out = { ...out, conditions: next }; }
    return out;
  });
}

/** The turn moved forward from `endedId` to `startedId`. */
export function tickConditions(characters: Character[], endedId: string, startedId: string): TickResult {
  const expired: TickResult["expired"] = [];
  const aurasEnded: TickResult["aurasEnded"] = [];
  const lightsOut: TickResult["lightsOut"] = [];
  const next = characters.map((c) => {
    let out = c;
    if (c.id === startedId && c.light) { const r = tickLight(out); if (r.ended) lightsOut.push({ characterName: c.name, light: r.ended.name }); if (r.light !== out.light) { const { light: _drop, ...rest } = out; out = r.light ? { ...rest, light: r.light } : rest as Character; } }
    if (c.id === startedId && (c.auras || []).length) { const r = tickAuras(out); if (r.expired.length || r.auras.some((x, i) => x !== (out.auras || [])[i])) out = { ...out, auras: r.auras }; r.expired.forEach((a) => aurasEnded.push({ characterName: c.name, aura: a.name })); }
    if ((c.conditions || []).length) { const r = tickList(out.conditions || [], c.id, endedId, startedId); if (r.expired.length || r.conditions.length !== (out.conditions || []).length || r.conditions.some((x, i) => x !== (out.conditions || [])[i])) out = { ...out, conditions: r.conditions }; r.expired.forEach((name) => expired.push({ characterName: c.name, condition: name })); }
    return out;
  });
  return { characters: next, expired, aurasEnded, lightsOut };
}
